import type { LabelHTMLAttributes } from 'react';
import { cx } from './cx.js';
import { FieldLabel } from './FieldLabel.js';

/**
 * A setting that is on or off right now.
 *
 * A switch, not a tick box. A checkbox is a thing you tick and then submit; a
 * switch is a thing that is already true or already false, and every setting in
 * this game applies the moment it is touched. So the element is a real
 * `<input type="checkbox">` wearing `role="switch"`: the platform keeps the
 * keyboard behavior and the focus ring it already gets right, and the role
 * fixes what a screen reader says about it.
 *
 * `brain/brand/BRAND.md` has named this as one of the three shapes that carry
 * every setting, "defined once in its own component under `ui/`", since before
 * this file existed. It was inside `SettingsList.tsx` instead, and
 * `/dev/lookbook` imported it out of that feature file to demo it. The brand
 * document and the folder disagreed, and the folder was wrong.
 *
 * The role, the native input and the track are all sealed in here. `className`
 * reaches the `<label>` and nothing else, so no call site can take the role
 * off, and none has to remember to put it on.
 *
 * The name and its hint are `FieldLabel`, shared with `Choice` and `Level`.
 */

export interface SwitchProps extends Omit<
  LabelHTMLAttributes<HTMLLabelElement>,
  'className' | 'style' | 'children' | 'onChange'
> {
  /** What the setting is called. Already translated. */
  readonly label: string;
  /** One line under it, for a setting whose name is not the whole story. */
  readonly hint?: string | undefined;
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  /** Layout and state only. The peg and the track are not yours. */
  readonly className?: string | undefined;
}

const ROW = 'flex min-h-13 cursor-pointer items-center gap-4 py-2 text-ink';

/*
 * A chip, the way every control in this brand is a chip: a white face, a 2px
 * ink stroke, and `shadow-xs shadow-ink`, with a solid ink dot riding inside it. On
 * flips the fill — a gray-700 face under a white dot — so the two states are
 * one control at one visual weight, not a pale recess that becomes a slab.
 *
 * This is the third draft, and the first two each teach one constraint. The
 * first was a chip whose knob wore its own ink ring: two rings of one weight
 * 2px apart read as an eye, not a control. The dot is *solid* now, which is
 * what makes the chip track safe to bring back — one ink shape, no ring
 * inside a ring. The second draft was the `Slider`'s groove, a `ground-dark`
 * recess with a hairline edge, and it failed in the other direction: on a
 * white panel the recess all but vanished, so an off switch read as a lone
 * circle floating beside its label, and the on state — a bare ink pill with a
 * plain white ball, no stroke doing visible work, no shadow — read as a stock
 * toggle from someone else's app. The chip look is the brand's signature, and
 * the switch was the one control on the panel not wearing it.
 *
 * On is GRAY-700: not lemon, and not full ink either. Lemon marks the one
 * thing a screen is asking for, and a settings panel is eight answers a
 * player has already given — eight lemon tracks would spend the loudest color
 * in the palette on the quietest surface in the app. Full ink was the first
 * shipped fill and lasted a day: judged too heavy at panel dose on the
 * workshop bench (2026-08-27), where gray-700 won over the whole palette.
 * One stop down keeps the fill unmistakable — 8.6:1 under the white dot —
 * while the panel stays quiet, and the ink stroke both states wear keeps the
 * chip's drawn edge on the softer face.
 *
 * **The geometry is one arithmetic, not four tastes**, the way `Slider`'s is.
 * The track is 32 by 56 with a 2px stroke, so the well is 28 by 52; the dot
 * is 20 and sits 4px in, which leaves 4px of air on every side; and the
 * travel is what is left over, 24px, which is why `translate-x-6` parks it at
 * the far end. Change one and the other three move with it. The air matters:
 * the dot at 20 in a 28px well is drawn geometry with room around it, where a
 * 22px ball in the same well was the crammed proportion of the system toggle
 * this component must not resemble.
 *
 * The colors stamp and the dot settles. The dot slides on `transition-knob` —
 * `translate` only — because under `transition-control` the slide was dead:
 * that list covers `transform`, and Tailwind 4's translate utilities write
 * the CSS `translate` property, so the knob snapped while the colors faded.
 * And the colors must not fade: dot and face swap dark for white in opposite
 * directions, so a simultaneous cross-fade passes both through the same
 * mid-gray and the dot vanishes into the track halfway through every toggle.
 * Snapped, the state is legible the instant it is pressed, and the motion is
 * all in the one thing that moves. The track transitions nothing, which also
 * makes the press the Button's settle: the chip drops onto its own shadow,
 * instantly, the way every chip in the brand presses. `motion-reduce` stills
 * the slide, which is the whole of the animation.
 *
 * The ring is the base layer's, not this component's. Nothing here suppresses
 * `:focus-visible`, so the rule in `styles.css` draws it and the surface the
 * switch sits on gets to color it.
 */
const TRACK =
  'relative m-0 h-8 w-14 shrink-0 cursor-pointer appearance-none rounded-full border-2 border-ink bg-ground-light shadow-xs shadow-ink hover:not-checked:wash-ink active:translate-x-px active:translate-y-px active:shadow-none after:absolute after:top-1/2 after:left-1 after:size-5 after:-translate-y-1/2 after:rounded-full after:bg-ink after:transition-knob after:duration-160 after:ease-settle motion-reduce:after:transition-none checked:bg-gray-700 checked:after:translate-x-6 checked:after:bg-ground-light';

export function Switch({
  label,
  hint,
  checked,
  onChange,
  className,
  ...rest
}: SwitchProps): React.JSX.Element {
  return (
    <label className={cx(ROW, className)} {...rest}>
      <FieldLabel className="flex-1" hint={hint}>
        {label}
      </FieldLabel>
      <input
        type="checkbox"
        role="switch"
        className={TRACK}
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
    </label>
  );
}
